import React from "react";
//@ts-ignore
import { StriimChip, StriimTooltip } from "@striim/striim-ui";
import { EngineDetails, HealthStatus } from "core/services/copilotService";
import { useAppSelector } from "src/stores/redux/hooks";
import {
    selectCopilotUrl,
    selectEngineDetails,
    selectIsLoading
} from "src/stores/redux/features/striim-ai/striim-ai.slice";
import { AI_AGENT_NAME } from "../../ai-constants";

interface Props {
    healthStatus: HealthStatus | null;
}

type ChipVariant = "success" | "warning" | "error" | "default";

const isKnowledgebaseLoading = (loadedKb?: Record<string, boolean>): boolean => {
    if (!loadedKb || Object.keys(loadedKb).length === 0) return false;
    return Object.values(loadedKb).some(loaded => !loaded);
};

interface StatusConfig {
    variant: ChipVariant;
    label: string;
    tooltip: string;
}

function deriveStatus(
    healthStatus: HealthStatus | null,
    engine: EngineDetails | null,
    copilotUrl: string | null
): StatusConfig {
    // 1. Healthy — server is healthy and authenticated; show the connection target in the tooltip
    if (healthStatus?.status === "healthy" && healthStatus?.isAuth === true) {
        const isExisting = engine?.engineType === "existing";
        let tooltip = isExisting
            ? `Connected to ${engine?.striimAiUrl ?? copilotUrl ?? `${AI_AGENT_NAME}'s server`}`
            : [engine?.provider, engine?.model].filter(Boolean).join(" · ") || "Healthy";

        const isKbLoading = isKnowledgebaseLoading(healthStatus?.agent?.loaded_kb);
        if (isKbLoading) {
            tooltip += " · Knowledgebase is loading...";
        }
        return { variant: "success", label: "Healthy", tooltip };
    }

    // 2. Auth failed — server is reachable/healthy but authentication did not succeed
    if (healthStatus?.status === "healthy" && !healthStatus?.isAuth) {
        return {
            variant: "error",
            label: "Auth failed",
            tooltip: `${AI_AGENT_NAME}'s authentication failed. Please reconfigure ${AI_AGENT_NAME} with valid credentials.`
        };
    }

    // 3. Not connected — no health status at all
    if (!copilotUrl) {
        return {
            variant: "default",
            label: "Not connected",
            tooltip: `${AI_AGENT_NAME} is not connected. Click the Settings icon on the right to configure ${AI_AGENT_NAME}.`
        };
    }

    // 4. Misconfigured — configured (copilot URL present) but not healthy, including agent-level
    // errors (e.g. an invalid provider API key). Deliberately a friendly, generic message —
    // the raw agent error can be a long provider-specific exception string, not fit for end users.
    const status = healthStatus?.status ? ` (${healthStatus.status})` : "";
    return {
        variant: "warning",
        label: "Misconfigured",
        tooltip: copilotUrl
            ? `${AI_AGENT_NAME}'s configuration appears invalid — check your API key, model, or server URL${status}.`
            : `${AI_AGENT_NAME}'s configuration appears invalid — check your API key, model, or server URL${status}.`
    };
}

const AIStatusIndicator: React.FC<Props> = ({ healthStatus }) => {
    const copilotUrl = useAppSelector(selectCopilotUrl);
    const engine = useAppSelector(selectEngineDetails);
    const isLoading = useAppSelector(selectIsLoading);
    if (isLoading) {
        return (
            <span style={{ display: "inline-flex" }} data-test-id="connection-status">
                <StriimChip variant="default" label="Configuring" size="small" type="tag-chip" />
            </span>
        );
    }

    const { variant, label, tooltip } = deriveStatus(healthStatus, engine, copilotUrl);

    return (
        <StriimTooltip title={tooltip} arrow placement="bottom">
            <span style={{ display: "inline-flex" }} data-test-id="connection-status">
                <StriimChip variant={variant} label={label} size="small" type="tag-chip" />
            </span>
        </StriimTooltip>
    );
};

export default AIStatusIndicator;
